import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { ErrorText, Input } from '@utilities';
import { parseNumber } from '../../utilities/helpers';
import styles from './_index.module.scss';

const PercentInputs = ({ onChangeValues, selectedAnswer }) => {
  const [heavy, setHeavy] = useState(selectedAnswer?.values?.[2] ?? '');
  const [medium, setMedium] = useState(selectedAnswer?.values?.[1] ?? '');
  const [light, setLight] = useState(selectedAnswer?.values?.[0] ?? '');

  useEffect(() => {
    setHeavy(selectedAnswer?.values?.[2] ?? '');
    setMedium(selectedAnswer?.values?.[1] ?? '');
    setLight(selectedAnswer?.values?.[0] ?? '');
  }, [selectedAnswer?.attributeId]);

  const hasAllRequiredFields = heavy !== '' && medium !== '' && light !== '';
  const hasNegativeValues = heavy < 0 || medium < 0 || light < 0;

  const totalPercentage = useMemo(() => {
    const heavyValue = Number(heavy) || 0;
    const mediumValue = Number(medium) || 0;
    const lightValue = Number(light) || 0;
    return heavyValue + mediumValue + lightValue;
  }, [heavy, medium, light]);

  const handleChange = (nextLight, nextMedium, nextHeavy) => {
    onChangeValues([parseNumber(nextLight), parseNumber(nextMedium), parseNumber(nextHeavy)]);
  };

  return (
    <div>
      <div className={styles['percent-inputs-config-block']}>
        <div>
          <Input
            className={classNames({
              [styles['percent-inputs-error']]: heavy < 0,
            })}
            label={'Heavy'}
            max={100}
            min={0}
            onChange={(val) => {
              setHeavy(val);
              handleChange(light, medium, val);
            }}
            placeholder="%"
            type="number"
            value={heavy}
          />
        </div>
        <div>
          <Input
            className={classNames({
              [styles['percent-inputs-error']]: medium < 0,
            })}
            label={'Medium'}
            max={100}
            min={0}
            onChange={(val) => {
              setMedium(val);
              handleChange(light, val, heavy);
            }}
            placeholder="%"
            type="number"
            value={medium}
          />
        </div>
        <div>
          <Input
            className={classNames({
              [styles['percent-inputs-error']]: light < 0,
            })}
            label={'Light'}
            max={100}
            min={0}
            onChange={(val) => {
              setLight(val);
              handleChange(val, medium, heavy);
            }}
            placeholder="%"
            type="number"
            value={light}
          />
        </div>
        <div
          className={classNames(styles['percent-inputs-total'], {
            [styles['percent-inputs-success']]: totalPercentage === 100 || !hasAllRequiredFields,
          })}
        >
          <span>{Number.isFinite(totalPercentage) ? `${totalPercentage}%` : '--'}</span>
          <span>&nbsp;of 100%</span>
        </div>
      </div>
      {hasNegativeValues && (
        <ErrorText className={styles['input-error-text']} text="Percentage cannot be negative" />
      )}
    </div>
  );
};

PercentInputs.propTypes = {
  onChangeValues: PropTypes.func.isRequired,
  selectedAnswer: PropTypes.shape({
    attributeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  }).isRequired,
};

export default PercentInputs;
